import React, { useContext, useEffect, useState } from "react";
import { Link, RouteComponentProps } from "@reach/router";
import styled from "styled-components";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { Button } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { CurrencyInput } from "../../components/common/CurrencyInput";
import { gql } from "apollo-boost";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  BankTransactionResult,
  MutationConfirmWithdrawFiatArgs,
  MutationWithdrawFiatArgs,
} from "../../../../store/generated-models";
import { useSnackbar } from "notistack";
import { Dropdown } from "../../components/common/Dropdown";
import { AuthContext } from "../../../../core/providers/AuthProvider";
import { LoadWrap, gqlGetSupportedCurrenciesWithRates } from "./Withdraw";
import CircularProgress from "@material-ui/core/CircularProgress";
import { round, OERO_PRECISION } from "../../../../core/helpers/Currencies";
import {withTranslation} from "react-i18next";
import {t} from "i18next";

const Row = styled.div`
  display: flex;
`;

const ConfirmWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledTextField = styled(TextField)`
  background-color: white;
  .MuiOutlinedInput-root {
    :hover {
      .MuiOutlinedInput-notchedOutline {
        border-color: #9e9e9e;
      }
    }
  }
`;

const Confirm = styled.div`
  border: 1px solid #b5b5b5;
  border-radius: 5px;
  padding: 40px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Alert = styled.span`
  color: ${(props) => props.theme.custom.palette.alert};
  font-weight: bold;
`;

const LinkStyled = styled(Link)`
  text-decoration: none;
  color: ${(props) => props.theme.custom.palette.alert};
  &:hover {
    opacity: 0.87;
  }
`;

const gqlWithdrawFiat = gql`
  mutation withdrawFiat(
    $immutableAmount: Int!
    $bankAccountId: String!
    $comment: String
    $fiatAmount: Float!
    $fiatCurrency: String!
    $transactionType: String!
    $oeroAmount: Float!
  ) {
    withdrawFiat(
      immutableAmount: $immutableAmount
      bankAccountId: $bankAccountId
      comment: $comment
      fiatAmount: $fiatAmount
      fiatCurrency: $fiatCurrency
      transactionType: $transactionType
      oeroAmount: $oeroAmount
    ) {
      id
      transaction {
        firstUserName
        lastUserName
        currency
        amount
        comment
      }
    }
  }
`;

const gqlConfirmWithdrawFiat = gql`
  mutation confirmWithdrawFiat($id: String) {
    confirmWithdrawFiat(id: $id) {
      transaction {
        firstUserName
        lastUserName
        currency
        amount
        comment
        transactionCode
      }
    }
  }
`;

const gqlMyBankAccounts = gql`
  {
    myBankAccounts {
      userBankAccountId
      accountAlias
      bankName
      iban
      bic
      address
      ownerName
    }
  }
`;

const DEF_CURR_NAME = "EUR";

const initFields = {
  transactionType: "",
  bankAccountId: "",
  fromAmount: "",
  fromCurrency: "OERO",
  toAmount: "",
  toCurrency: DEF_CURR_NAME,
  comment: "",
};

const defaultCurrency = {
  minAmount: 0,
  name: DEF_CURR_NAME,
  withdrawRate: 0,
};

interface ComponentProps extends RouteComponentProps {}

export const BankTransaction: React.FC<ComponentProps> = () => {
  const authContext = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [withdrawFiat, { loading: withdrawFiatLoading }] = useMutation<
    { withdrawFiat: BankTransactionResult },
    MutationWithdrawFiatArgs
  >(gqlWithdrawFiat);

  const [confirmWithdrawFiat, { loading: confirmWithdrawFiatLoading }] =
    useMutation<
      { confirmWithdrawFiat: BankTransactionResult },
      MutationConfirmWithdrawFiatArgs
    >(gqlConfirmWithdrawFiat);

  const { data: bankAccountsData } = useQuery(gqlMyBankAccounts, {
    displayName: "gqlMyBankAccounts",
  });

  const [fields, setFields] = useState<any>({ ...initFields });
  const [errors, setErrors] = useState<any>({});

  const [fromAmountValue, setFromAmountValue] = useState(0);
  const [toAmountValue, setToAmountValue] = useState(0);

  const [currencies, setCurrencies] = useState<any[]>([{ id: DEF_CURR_NAME }]);
  const [immutableAmount, setImmutableAmount] = useState(0);
  const [status, setStatus] = useState("");
  const [txId, setTxId] = useState("");
  const [txInfo, setTxInfo] = useState<any>({});
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [selectedCurrency, setSelectedCurrency] =
    useState<any>(defaultCurrency);

  const { data, error, loading, refetch } = useQuery(
    gqlGetSupportedCurrenciesWithRates
  );

  useEffect(() => {
    refetch().catch((err) => console.warn(err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (bankAccountsData) {
      setBankAccounts(
        bankAccountsData.myBankAccounts.map((el: any) => {
          return {
            id: el.userBankAccountId,
            value: el.accountAlias,
          };
        })
      );
    }
  }, [bankAccountsData]);

  const getSelectedCurrency = (cur: string) => {
    let selCur = data.getSupportedCurrenciesWithRates.fiat.find(
      (el: any) => el.name === cur
    );
    if (!selCur) selCur = defaultCurrency;
    return selCur;
  };

  const setInitial = () => {
    setFields({ ...initFields });
    setStatus("");
    setFromAmountValue(0);
    setToAmountValue(0);
    setImmutableAmount(0);
    setTxId("");
    setTxInfo({});
    setSelectedCurrency(getSelectedCurrency(DEF_CURR_NAME));
  };

  const submitWithdraw = async () => {
    let err: any = {};
    if (!fields.transactionType) err.transactionType = "required";
    if (!fields.bankAccountId) err.bankAccountId = "required";
    if (!fromAmountValue) err.fromAmount = "required";
    if (!toAmountValue) err.toAmount = "required";

    if (
      toAmountValue &&
      fields.toCurrency &&
      authContext.settings &&
      authContext.settings.restrictMinAmounts &&
      toAmountValue < selectedCurrency.minAmount
    ) {
      err.toAmount = `minimum ${selectedCurrency.minAmount} ${selectedCurrency.name}`;
    }

    if (
      !err.fromAmount &&
      authContext.user.state.contractBalance < fromAmountValue
    ) {
      err.fromAmount = "Insufficient funds";
    }

    setErrors({ ...err });

    if (Object.keys(err).length !== 0) {
      enqueueSnackbar("Correct errors to continue", { variant: "error" });
      return;
    }

    let errMessage = "";

    try {
      const res = await withdrawFiat({
        variables: {
          immutableAmount: immutableAmount,
          bankAccountId: fields.bankAccountId,
          comment: fields.comment,
          fiatAmount: toAmountValue,
          fiatCurrency: fields.toCurrency,
          transactionType: fields.transactionType,
          oeroAmount: fromAmountValue,
        },
      });
      if (res.data.withdrawFiat.id) {
        setTxId(res.data.withdrawFiat.id);
        setTxInfo(res.data.withdrawFiat.transaction);
        setStatus("confirm");
        return;
      } else {
        console.warn("submitWithdraw res:", res);
      }
    } catch (error) {
      try {
        const errorCode = error.graphQLErrors[0].extensions.code;
        if (errorCode === "auth.access_denied") {
          errMessage = "Access denied";
        } else if (errorCode === "core.insufficient_funds") {
          errMessage = "Insufficient funds";
        }
        if (!errMessage && error.graphQLErrors[0].message)
          errMessage = error.graphQLErrors[0].message;
      } catch (ignored) {}
    }

    if (!errMessage) errMessage = "Unknown error";
    enqueueSnackbar(errMessage, { variant: "error" });
  };

  const submitConfirmWithdraw = async () => {
    let errMessage = "";

    try {
      const res = await confirmWithdrawFiat({
        variables: {
          id: txId,
        },
      });
      if (res.data.confirmWithdrawFiat.transaction.transactionCode) {
        setTxInfo(res.data.confirmWithdrawFiat.transaction);
        setStatus("done");
        return;
      } else {
        console.warn("submitConfirmWithdraw res:", res);
      }
    } catch (error) {
      try {
        const errorCode = error.graphQLErrors[0].extensions.code;
        if (errorCode === "auth.access_denied") {
          errMessage = "Access denied";
        } else if (errorCode === "core.insufficient_funds") {
          errMessage = "Insufficient funds";
        }
        if (!errMessage && error.graphQLErrors[0].message)
          errMessage = error.graphQLErrors[0].message;
      } catch (ignored) {}
    }

    if (!errMessage) errMessage = "Unknown error";
    enqueueSnackbar(errMessage, { variant: "error" });
  };

  useEffect(() => {
    if (!data || !data.getSupportedCurrenciesWithRates) return;

    setCurrencies(
      data.getSupportedCurrenciesWithRates.fiat.map((el: any) => {
        return {
          id: el.id,
          value: el.name,
        };
      })
    );

    setSelectedCurrency(getSelectedCurrency(DEF_CURR_NAME));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const processValues = (params: any) => {
    if (params.immutable !== undefined) setImmutableAmount(params.immutable);

    let newFields: any = {};
    if (params.fromAmount !== undefined)
      newFields.fromAmount = params.fromAmount;
    if (params.toAmount !== undefined) newFields.toAmount = params.toAmount;
    if (params.toCurrency !== undefined)
      newFields.toCurrency = params.toCurrency;

    let rateVal: number;
    if (params.withdrawRate !== undefined) {
      const selCurr = getSelectedCurrency(params.toCurrency);
      setSelectedCurrency(selCurr);
      rateVal = selCurr.withdrawRate;
    } else {
      rateVal = selectedCurrency.withdrawRate;
    }

    const fromAmountVal =
      params.fromAmount !== undefined ? params.fromAmount : fields.fromAmount;
    const toAmountVal =
      params.toAmount !== undefined ? params.toAmount : fields.toAmount;
    const immutableAmountVal =
      params.immutable !== undefined ? params.immutable : immutableAmount;

    let fromAmt = fromAmountVal === null ? null : parseFloat(fromAmountVal);
    let toAmt = toAmountVal === null ? null : parseFloat(toAmountVal);

    if (fromAmt === null || toAmt === null || !rateVal) {
      setFromAmountValue(0);
      setToAmountValue(0);
      return;
    }

    if (immutableAmountVal === 0) {
      if (isNaN(fromAmt)) {
        setFromAmountValue(0);
        setToAmountValue(0);
        newFields.toAmount = "";
        setFields({ ...fields, ...newFields });
        return;
      }

      fromAmt = round(fromAmt, OERO_PRECISION);
      const calc = round(fromAmt / rateVal, selectedCurrency.precision);
      // const calc = fromAmt / rateVal;
      setFromAmountValue(fromAmt);
      setToAmountValue(calc);
      newFields.toAmount = calc.toString();
      setFields({ ...fields, ...newFields });
    } else {
      if (isNaN(toAmt)) {
        setFromAmountValue(0);
        setToAmountValue(0);
        newFields.fromAmount = "";
        setFields({ ...fields, ...newFields });
        return;
      }

      toAmt = round(toAmt, selectedCurrency.precision);
      const calc = round(toAmt * rateVal, OERO_PRECISION);
      // const calc = toAmt * rateVal;
      setFromAmountValue(calc);
      setToAmountValue(toAmt);
      newFields.fromAmount = calc.toString();
      setFields({ ...fields, ...newFields });
    }
  };

  const handleInputFromAmountChange = (val: string) => {
    processValues({
      immutable: 0,
      fromAmount: val,
    });
  };

  const handleInputToAmountChange = (val: string) => {
    processValues({
      immutable: 1,
      toAmount: val,
    });
  };

  const handleToCurrencyChange = (val: string) => {
    processValues({
      withdrawRate: true,
      toCurrency: val,
    });
  };

  if (error) {
    return <LoadWrap>Internal server error. Try again later</LoadWrap>;
  }

  if (!data || loading) {
    return (
      <LoadWrap>
        <CircularProgress />
      </LoadWrap>
    );
  }

  // Render CONFIRM step
  if (status === "confirm") {
    return (
      <ConfirmWrap>
        <Confirm>
          <Typography variant={"h2"} style={{ textAlign: "center" }}>
            {t('user.withdraw.please_confirm_transaction')}
          </Typography>

          <div style={{ marginTop: "24px" }}>
            <Typography variant={"h3"}>
              {t('user.withdraw.withdraw_amount')} {txInfo.amount}&nbsp;{txInfo.currency}
            </Typography>
          </div>

          <Row style={{ marginTop: "24px", width: "100%" }}>
            <Button
              variant="contained"
              color="primary"
              size={"large"}
              style={{
                minWidth: "120px",
                maxWidth: "240px",
                width: "100%",
              }}
              onClick={() => submitConfirmWithdraw()}
              disabled={confirmWithdrawFiatLoading}
            >
              {t('user.withdraw.confirm')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size={"large"}
              style={{ minWidth: "120px", marginLeft: "20px" }}
              onClick={() => setInitial()}
            >
              {t('user.withdraw.cancel')}
            </Button>
          </Row>
        </Confirm>
      </ConfirmWrap>
    );
  }

  // Render DONE step
  if (status === "done") {
    return (
      <ConfirmWrap>
        <Confirm>
          <Typography variant={"h2"} style={{ textAlign: "center" }}>
            {t('user.withdraw.your_transaction_is')}
          </Typography>
          <Typography variant={"h2"} style={{ textAlign: "center" }}>
            <Alert>{t('user.withdraw.in_processing')}</Alert>
          </Typography>
          <LinkStyled to={"/private/history"} style={{ marginTop: "24px" }}>
            {t('user.withdraw.click_here')}
          </LinkStyled>
        </Confirm>
      </ConfirmWrap>
    );
  }

  // Render INITIAL step
  return (
    <>
      <Grid container spacing={3} style={{ maxWidth: "1000px" }}>
        <Grid item xs={12} md={6}>
          <Dropdown
            error={!!errors.transactionType}
            helperText={errors.transactionType}
            label={t('user.withdraw.method_of_withdrawal')}
            fullWidth
            values={[
              { id: "WITHDRAW_BANK", value: "International Bank transfer" },
              { id: "WITHDRAW_SEPA", value: "SEPA transfer" },
            ]}
            style={{ backgroundColor: "white" }}
            value={fields.transactionType}
            onChange={(val) => setFields({ ...fields, transactionType: val })}
          />
        </Grid>
        {/*<Grid item xs={12} style={{padding: 0}}/>*/}
        <Grid item xs={12} md={6}>
          {bankAccounts.length ? (
            <Dropdown
              error={!!errors.bankAccountId}
              helperText={errors.bankAccountId}
              label={t('user.withdraw.bank_account')}
              fullWidth
              style={{ backgroundColor: "white" }}
              values={bankAccounts}
              value={fields.bankAccountId}
              onChange={(val) => setFields({ ...fields, bankAccountId: val })}
            />
          ) : (
            <LinkStyled
              to={"/private/settings/1"}
              style={{ marginTop: "24px" }}
            >
              {t('user.withdraw.add_bank_account')}
            </LinkStyled>
          )}
        </Grid>
        <Grid item xs={12} style={{ padding: 0 }} />
        <Grid item xs={12} sm={6}>
          <CurrencyInput
            error={!!errors.fromAmount}
            helperText={errors.fromAmount}
            label={t('user.withdraw.from')}
            amount={fields.fromAmount}
            onAmountChange={(val) => handleInputFromAmountChange(val)}
            currency={fields.fromCurrency}
            onCurrencyChange={(val) =>
              setFields({ ...fields, fromCurrency: val })
            }
            currencies={[{ id: "OERO" }]}
            fullWidth
            dropdownDisabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CurrencyInput
            error={!!errors.toAmount}
            helperText={errors.toAmount}
            label={t('user.withdraw.to')}
            amount={fields.toAmount}
            onAmountChange={(val) => handleInputToAmountChange(val)}
            currency={fields.toCurrency}
            onCurrencyChange={(val) => handleToCurrencyChange(val)}
            currencies={currencies}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            error={!!errors.comment}
            helperText={errors.comment}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            rowsMax={6}
            label={t('user.withdraw.comment')}
            value={fields.comment}
            //onChange={(event) => setComment(event.target.value)}
            onChange={(e) => setFields({ ...fields, comment: e.target.value })}
          />
        </Grid>
      </Grid>

      <Row style={{ marginTop: "24px" }}>
        <Button
          variant="contained"
          color="primary"
          size={"large"}
          style={{ minWidth: "240px", minHeight: "50px" }}
          onClick={() => submitWithdraw()}
          disabled={withdrawFiatLoading}
        >
          {t('user.withdraw.next')}
        </Button>
      </Row>
    </>
  );
};

export default withTranslation()(BankTransaction)
